import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit2, Trash2, Save, X, Users, Package, IndianRupee, ChevronDown, ChevronUp, Search, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Customers = () => {
  const navigate = useNavigate();
  // Customers state
  const [customers, setCustomers] = useState([]);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Add customer dialog
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerItems, setNewCustomerItems] = useState([{ item_name: "", price: "" }]);
  
  // Edit customer dialog
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editCustomerName, setEditCustomerName] = useState("");
  const [editCustomerItems, setEditCustomerItems] = useState([]);
  
  // Delete confirmation
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  
  // WO Items state
  const [woItems, setWoItems] = useState([]);
  const [newItemName, setNewItemName] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [editItemName, setEditItemName] = useState("");
  const [deleteItemId, setDeleteItemId] = useState(null);

  useEffect(() => {
    fetchCustomers();
    fetchWoItems();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to load customers");
    }
  };
  
  const fetchWoItems = async () => {
    try {
      const response = await axios.get(`${API}/wo-items`);
      setWoItems(response.data);
    } catch (error) {
      console.error("Error fetching WO items:", error);
    }
  };

  // Customer handlers
  const handleAddCustomer = async () => {
    if (!newCustomerName.trim()) {
      toast.error("Please enter a company name");
      return;
    }

    // Filter out empty items
    const validItems = newCustomerItems.filter(item => item.item_name.trim());

    try {
      await axios.post(`${API}/customers`, { 
        name: newCustomerName.trim(),
        items: validItems.map(item => ({
          item_name: item.item_name.trim(),
          price: parseFloat(item.price) || 0
        }))
      });
      toast.success("Customer added successfully!");
      setNewCustomerName("");
      setNewCustomerItems([{ item_name: "", price: "" }]);
      setShowAddCustomer(false);
      fetchCustomers();
    } catch (error) {
      console.error("Error adding customer:", error);
      toast.error(error.response?.data?.detail || "Failed to add customer");
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editCustomerName.trim()) {
      toast.error("Please enter a company name");
      return;
    }

    const validItems = editCustomerItems.filter(item => item.item_name.trim());

    try {
      await axios.put(`${API}/customers/${editingCustomer.id}`, {
        name: editCustomerName.trim(),
        items: validItems.map(item => ({
          item_name: item.item_name.trim(),
          price: parseFloat(item.price) || 0
        }))
      });
      toast.success("Customer updated successfully!");
      setEditingCustomer(null);
      fetchCustomers();
    } catch (error) {
      console.error("Error updating customer:", error);
      toast.error(error.response?.data?.detail || "Failed to update customer");
    }
  };

  const handleDeleteCustomer = async () => {
    if (!deleteCustomerId) return;

    try {
      await axios.delete(`${API}/customers/${deleteCustomerId}`);
      toast.success("Customer deleted successfully!");
      setDeleteCustomerId(null);
      fetchCustomers();
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer");
    }
  };

  const openEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setEditCustomerName(customer.name);
    setEditCustomerItems(customer.items?.length > 0 ? [...customer.items] : [{ item_name: "", price: "" }]);
  };

  // Item row handlers for add dialog
  const addNewItemRow = () => {
    setNewCustomerItems([...newCustomerItems, { item_name: "", price: "" }]);
  };

  const removeNewItemRow = (index) => {
    if (newCustomerItems.length > 1) {
      setNewCustomerItems(newCustomerItems.filter((_, i) => i !== index));
    }
  };

  const updateNewItemRow = (index, field, value) => {
    const updated = [...newCustomerItems];
    updated[index][field] = value;
    setNewCustomerItems(updated);
  };

  // Item row handlers for edit dialog
  const addEditItemRow = () => {
    setEditCustomerItems([...editCustomerItems, { item_name: "", price: "" }]);
  };

  const removeEditItemRow = (index) => {
    if (editCustomerItems.length > 1) {
      setEditCustomerItems(editCustomerItems.filter((_, i) => i !== index));
    }
  };

  const updateEditItemRow = (index, field, value) => {
    const updated = [...editCustomerItems];
    updated[index][field] = value;
    setEditCustomerItems(updated);
  };

  // WO Item handlers
  const handleAddWoItem = async () => {
    if (!newItemName.trim()) {
      toast.error("Please enter an item name");
      return;
    }

    try {
      await axios.post(`${API}/wo-items`, { item_name: newItemName.trim() });
      toast.success("Item added successfully!");
      setNewItemName("");
      fetchWoItems();
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error(error.response?.data?.detail || "Failed to add item");
    }
  };

  const handleUpdateWoItem = async () => {
    if (!editItemName.trim()) {
      toast.error("Please enter an item name");
      return;
    }

    try {
      await axios.put(`${API}/wo-items/${editingItem.id}`, { item_name: editItemName.trim() });
      toast.success("Item updated successfully!");
      setEditingItem(null);
      setEditItemName("");
      fetchWoItems();
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error(error.response?.data?.detail || "Failed to update item");
    }
  };

  const handleDeleteWoItem = async () => {
    if (!deleteItemId) return;

    try {
      await axios.delete(`${API}/wo-items/${deleteItemId}`);
      toast.success("Item deleted successfully!");
      setDeleteItemId(null);
      fetchWoItems();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  // Filter customers by search
  const filteredCustomers = customers.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return c.name?.toLowerCase().includes(term) ||
      c.items?.some(item => item.item_name?.toLowerCase().includes(term));
  });

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/workorder")} variant="ghost" className="text-gray-400 hover:text-white" data-testid="back-to-workorder">
              <ArrowLeft className="h-5 w-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-cyan-400 flex items-center gap-3">
                <Users className="h-8 w-8" />
                WO Database
              </h1>
              <p className="text-gray-400 mt-1">Manage customers, items & pricing</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="customers" className="w-full">
          <TabsList className="bg-gray-800/50 border border-gray-700 mb-4">
            <TabsTrigger value="customers" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              <Users className="h-4 w-4 mr-2" />
              Customers
            </TabsTrigger>
          </TabsList>

          {/* Customers Tab */}
          <TabsContent value="customers">
            {/* Search and Add */}
            <Card className="mb-4 bg-gray-800/50 border border-gray-700">
              <CardContent className="p-4">
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label className="text-cyan-400 text-sm">Search</Label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search by company name or item..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-gray-900 border-gray-600 text-white"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowAddCustomer(true)}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Customer
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Customer List */}
            <Card className="bg-gray-800/50 border border-gray-700">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-cyan-400">
                  Customers ({filteredCustomers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {filteredCustomers.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Users className="h-16 w-16 mx-auto mb-4 text-gray-500" />
                    <p className="text-xl">No customers found</p>
                    <p className="text-sm mt-2">Add your first customer using the button above</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        className="border border-gray-700 rounded-lg overflow-hidden"
                      >
                        {/* Customer Header */}
                        <div 
                          className="flex items-center justify-between p-4 bg-gray-900/50 cursor-pointer hover:bg-gray-800/50 transition-all"
                          onClick={() => setExpandedCustomer(expandedCustomer === customer.id ? null : customer.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "transition-transform",
                              expandedCustomer === customer.id ? "rotate-180" : ""
                            )}>
                              <ChevronDown className="h-5 w-5 text-cyan-400" />
                            </div>
                            <div>
                              <p className="text-white text-lg font-bold">{customer.name}</p>
                              <p className="text-gray-400 text-sm">
                                {customer.items?.length || 0} item(s)
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              onClick={() => openEditCustomer(customer)}
                              size="sm"
                              variant="outline"
                              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => setDeleteCustomerId(customer.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Customer Items (Expanded) */}
                        {expandedCustomer === customer.id && (
                          <div className="p-4 bg-gray-800/50">
                            {customer.items && customer.items.length > 0 ? (
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-gray-600">
                                    <TableHead className="text-gray-300">Item Name</TableHead>
                                    <TableHead className="text-gray-300 text-right">Price (₹)</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {customer.items.map((item, idx) => (
                                    <TableRow key={idx} className="border-gray-600">
                                      <TableCell className="text-white font-medium">
                                        <div className="flex items-center gap-2">
                                          <Package className="h-4 w-4 text-cyan-400" />
                                          {item.item_name}
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <span className="text-cyan-400 font-bold flex items-center justify-end gap-1">
                                          <IndianRupee className="h-4 w-4" />
                                          {item.price?.toFixed(2) || "0.00"}
                                        </span>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <p className="text-gray-500 text-center py-4">No items added for this customer</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Customer Dialog */}
      <Dialog open={showAddCustomer} onOpenChange={setShowAddCustomer}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add New Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Company Name */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
              <Label className="text-cyan-400 font-bold text-lg">Company Name *</Label>
              <Input
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder="Enter company name"
                className="bg-gray-900 border-gray-600 text-white mt-2 text-lg"
              />
            </div>
            
            {/* Items Table */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-cyan-400 font-bold">Items & Prices</Label>
                <Button
                  onClick={addNewItemRow}
                  size="sm"
                  variant="outline"
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Row
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-gray-300">Item Name</TableHead>
                    <TableHead className="text-gray-300">Price (₹)</TableHead>
                    <TableHead className="text-gray-300 w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newCustomerItems.map((item, idx) => (
                    <TableRow key={idx} className="border-gray-700">
                      <TableCell>
                        <Input
                          value={item.item_name}
                          onChange={(e) => updateNewItemRow(idx, 'item_name', e.target.value)}
                          placeholder="Item name"
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => updateNewItemRow(idx, 'price', e.target.value)}
                          placeholder="0.00"
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => removeNewItemRow(idx)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          disabled={newCustomerItems.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCustomer(false)} className="border-gray-600 text-gray-300">
              Cancel
            </Button>
            <Button onClick={handleAddCustomer} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold">
              Add Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={!!editingCustomer} onOpenChange={(open) => !open && setEditingCustomer(null)}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Company Name */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
              <Label className="text-cyan-400 font-bold text-lg">Company Name *</Label>
              <Input
                value={editCustomerName}
                onChange={(e) => setEditCustomerName(e.target.value)}
                placeholder="Enter company name"
                className="bg-gray-900 border-gray-600 text-white mt-2 text-lg"
              />
            </div>
            
            {/* Items Table */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-cyan-400 font-bold">Items & Prices</Label>
                <Button
                  onClick={addEditItemRow}
                  size="sm"
                  variant="outline"
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Row
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-gray-300">Item Name</TableHead>
                    <TableHead className="text-gray-300">Price (₹)</TableHead>
                    <TableHead className="text-gray-300 w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editCustomerItems.map((item, idx) => (
                    <TableRow key={idx} className="border-gray-700">
                      <TableCell>
                        <Input
                          value={item.item_name}
                          onChange={(e) => updateEditItemRow(idx, 'item_name', e.target.value)}
                          placeholder="Item name"
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => updateEditItemRow(idx, 'price', e.target.value)}
                          placeholder="0.00"
                          className="bg-gray-900 border-gray-600 text-white"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => removeEditItemRow(idx)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          disabled={editCustomerItems.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCustomer(null)} className="border-gray-600 text-gray-300">
              Cancel
            </Button>
            <Button onClick={handleUpdateCustomer} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Confirmation */}
      <AlertDialog open={!!deleteCustomerId} onOpenChange={(open) => !open && setDeleteCustomerId(null)}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Customer</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to delete this customer? This will also remove all associated items and prices. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600 border-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCustomer} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Item Confirmation */}
      <AlertDialog open={!!deleteItemId} onOpenChange={(open) => !open && setDeleteItemId(null)}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Item</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to delete this item? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600 border-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteWoItem} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Customers;
